<template>
  <header class="search header">
    <div class="header__container tw-flexbox-row-align-center-justify-between">
      <nav class="left-column tw-flexbox-row-align-end-justify-between">
        <router-link
          :to="logoUrl"
          @click.native="handleLogoClicked"
        >
          <acv-auctions-primary-light-svg
            class="acv-logo"
            alt="The logo of ACV Auctions"
          />
        </router-link>
        <div
          class="header-links tw-hidden tw-whitespace-nowrap tw-md:tw-pl-4 tw-xl:tw-pl-8"
          :class="[isSearchExperience ? 'tw-lg:tw-flex' : 'tw-md:tw-flex']"
        >
          <!--TODO: The entire <li> should be clickable below, not just the <a>-->

          <template v-for="route in routes">
            <router-link
              v-if="route.path && !route.path.includes('http')"
              :key="route.path"
              :to="route.path"
              :active-class="'active'"
              class="tw-md:tw-mr-3 tw-xl:tw-mr-8 subtitle-1 header-link"
              :class="[
                route.cssClasses || [],
                route.key,
              ]"
              @click.native="trackLinkClicked"
            >
              {{ route.text }}
              <span
                v-if="route.badgeText"
                class="nav-badge"
              >
                {{ route.badgeText }}
              </span>
              <acv-menu-list
                v-if="route.dropdownItems
                  && route.dropdownItems.length"
                :key="route.path"
                :ref="`${route.key}-menu-list`"
                :items="route.dropdownItems"
                @item-selected="handleDropdownItemSelected"
              />
            </router-link>
            <a
              v-else
              :key="route.path"
              :href="route.path"
              class="tw-md:tw-mr-3 tw-xl:tw-mr-8 subtitle-1"
              target="_blank"
              rel="noopener noreferrer"
              :class="route.cssClasses || []"
              @click="!route.click && trackLinkClicked"
              @click.prevent="route.click"
            >
              <span>
                {{ route.text }}
              </span>
            </a>
          </template>
        </div>
      </nav>
      <div class="right-column tw-flexbox-row-align-center-justify-between">
        <router-link
          v-if="isDealerRewardsProgramEnabled"
          active-class="active"
          to="/rewards"
        >
          <div
            v-if="isDealerRewardsProgramEnabled"
            id="rewards-btn-lg"
            class="tw-sm:tw-hidden tw-2xl:tw-block"
          >
            <acv-button
              class="rewards-btn-lg !tw-pl-2 !tw-pr-2 !tw-pt-1 !tw-pb-1"
              variant="secondary"
              size="small"
              content="Button"
              @click="trackRewardsButtonEvent"
            >
              <template #prepend>
                <acv-icon
                  icon="emoji_events"
                  icon-style="outlined"
                  :animate-icon="false"
                />
              </template>
              <template #default>
                <div class="tw-text-base">
                  {{ rewardsBtnText }}
                </div>
              </template>
            </acv-button>
          </div>
          <div
            v-if="isDealerRewardsProgramEnabled"
            class="rewards-btn-sm tw-rounded-full tw-sm:tw-block tw-2xl:tw-hidden"
          >
            <acv-button
              class=""
              variant="secondary"
              size="small"
              content="Button"
              @click="trackRewardsButtonEvent"
            >
              <template #prepend>
                <acv-icon
                  icon="emoji_events"
                  icon-style="outlined"
                  :animate-icon="false"
                />
              </template>
            </acv-button>
          </div>
        </router-link>
        <acv-notifications-menu
          class="search notifications-menu search-notifications-menu"
          :icon="['far', 'bell']"
          :class="{ 'is-search': isSearchExperience }"
          :show-unseen-count="!isSearchExperience"
          :go-to-auction-on-notification-click="!isSearchExperience"
          @notificationClicked="handleNotificationClicked"
        />
        <acv-search-settings-dropdown-menu
          :full-name="currentUserFullName || email"
          :dealership-name="dealershipName"
        />
      </div>
    </div>
  </header>
</template>

<script>
import {
  AcvAuctionsPrimaryLightSvg, AcvButton, AcvIcon, AcvMenuList,
} from '@acv/shared-vuejs';
import AcvHeader from '@/components/Header.vue';
import AcvNotificationsMenu from '@/components/Notifications/NotificationsMenu.vue';
import AcvSearchSettingsDropdownMenu from '@/components/Search/SearchSettingsDropdownMenu.vue';
import Analytics from '@/mixins/Analytics';
import FeatureFlags from '@/mixins/FeatureFlags';
import getRewardsPointsLabel from '@/composables/loyalty/useRewardsPointsLabel';

export default {
  name: 'AcvSearchHeader',
  components: {
    AcvNotificationsMenu,
    AcvSearchSettingsDropdownMenu,
    AcvAuctionsPrimaryLightSvg,
    AcvButton,
    AcvIcon,
    AcvMenuList,
  },
  extends: AcvHeader,
  mixins: [Analytics, FeatureFlags],
  data: () => ({
    currentlySelectedMenuItem: '',
    rewardsBtnText: 'Dealer Rewards Program',
  }),
  computed: {
    isSearchExperience() {
      return ['Search', 'Marketplace'].includes(this.$route.name);
    },
    isDealerRewardsProgramEnabled() {
      return this.$store.state.rewards.isRewardsProgramEligible;
    },
    isOnEventsDirectory() {
      return this.$route.name === 'Marketplace'
        && this.enableSrpEventDirectory
        && !!this.showingEventsDirectory;
    },
  },
  watch: {
    isOnEventsDirectory: {
      immediate: true,
      deep: true,
      handler() {
        if (this.enableSrpEventDirectory) {
          const marketplaceEntry = this.routes.find((r) => r.text === 'Marketplace');

          if (this.isOnEventsDirectory) {
            // preselect on directory if applicable and change component refs
            this.currentlySelectedMenuItem = 'directory';
            marketplaceEntry.dropdownItems[1].defaultSelection = true;
            this.$refs[`${marketplaceEntry.key}-menu-list`]?.[0]?.setToProvidedItem(
              { id: 'menu-list-item-0' },
              { id: 'menu-list-item-1' },
            );
          } else if (this.$route.name === 'Marketplace') {
            // preselect on Search if applicable and change component refs
            this.currentlySelectedMenuItem = 'marketplace';
            marketplaceEntry.dropdownItems[0].defaultSelection = true;
            this.$refs[`${marketplaceEntry.key}-menu-list`]?.[0]?.setToProvidedItem(
              { id: 'menu-list-item-1' },
              { id: 'menu-list-item-0' },
            );
          } else {
            // no longer on marketplace or directory, uncheck dropdown items
            this.uncheckDropdownItemsForRoute(marketplaceEntry);
          }
        }
      },
    },
  },
  async mounted() {
    this.rewardsBtnText = await getRewardsPointsLabel();
  },
  methods: {
    trackRewardsButtonEvent() {
      this.track(this.eventNames.rewardsButtonClicked, {
        eventCategory: 'Rewards',
        eventAction: 'Rewards Button Clicked',
        eventLabel: 'Rewards Button Clicked',
      });
    },
    isLoyaltyProgressCardFeatureEnabled() {
      return this.featureFlags[this.featureFlagSlugs.enableAcvLoyaltyProgressCard];
    },
    handleNotificationClicked($event) {
      const {
        $event: { ctrlKey, metaKey, shiftKey },
        auctionId,
      } = $event;
      const auctionDetailUrl = `${window.location.origin}/auction/${auctionId}`;
      if (ctrlKey || metaKey) {
        window.open(auctionDetailUrl);
      } else if (shiftKey) {
        window.open(auctionDetailUrl, '_blank');
      } else {
        this.$root.$emit('acv::notificationClicked', $event);
      }
    },
    uncheckDropdownItemsForRoute(routeEntry) {
      this.$nextTick(() => {
        this.currentlySelectedMenuItem = '';
        if (this.$refs[`${routeEntry.key}-menu-list`]) {
          this.$refs[`${routeEntry.key}-menu-list`][0].currentSelection = null;
        }
      });
    },
    handleDropdownItemSelected($event = {}) {
      if (this.currentlySelectedMenuItem === $event?.event?.key) {
        return; // Do not re-select the same item
      }

      this.currentlySelectedMenuItem = $event.event.key; // retain the new selection

      if (this.currentlySelectedMenuItem === 'marketplace') {
        this.$store.dispatch('marketplaceView/updateShowingEventsDirectory', '');

        // navigate to Marketplace if not already there or if blank out query params, if present
        if (Object.keys(this.$route.query).length > 0
          || this.$route.name !== 'Marketplace'
        ) {
          this.$router.push({ name: 'Marketplace' });
        }
      } else if (this.currentlySelectedMenuItem === 'directory') {
        this.$store.dispatch('marketplaceView/updateShowingEventsDirectory', 'directory');

        // navigate to Marketplace showing the directory, if not on marketplace already
        if (this.$route.name !== 'Marketplace') {
          this.$router.push({ name: 'Marketplace', query: { d: 'directory' } });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/scss/search/fonts';

.rewards-btn-sm {
  :deep(.acv-button__prepend){
    margin-right: 0;
  }
}

.orange-outline {
  border: 1px solid rgb(242 101 34);
}

.header {
  width: 100%;
  border-bottom: 1px solid #ddd;
  z-index: 6;

  @apply tw-bg-white;

  &__container {
    padding: 0 30px;

    .left-column {
      .header-links {
        margin: 0 auto;
        height: 100%;

        a {
          padding: 1rem 0;
          display: inline-block;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          color: theme('colors.black.500');

          @include fonts.subtitle-1;

          &:hover {
            text-decoration: none;

            @apply tw-border-primary-base;
            @apply tw-border-b-2;
            @apply tw-text-black-670;
          }

          &.active {
            @apply tw-border-primary-base;
            @apply tw-border-b-2;
            @apply tw-text-primary-base;
          }
        }

        .marketplace-header-link {
          > :deep(.acv-menu-list) {
            display: none;
            margin-top: 1.1875rem;
          }

          &:hover,
          &:focus-within {
            :deep(.acv-menu-list) {
              display: block;
            }
          }
        }
      }

      .acv-logo {
        height: 60px;
        padding: 0.75rem 0;
      }

      .nav-badge {
        @apply tw-bg-error-base
        tw-text-white
        tw-py-1
        tw-px-2
        tw-ml-1.5
        tw-relative
        tw-bottom-0.5;

        border-radius: 60px;
        font-size: 10px;
      }
    }

    .right-column {
      .search-notifications-menu {
        ::v-deep .notification-badge {
          @apply tw-p-1;

          top: 0.2rem;
        }

        ::v-deep .btn {
          padding: 0;
        }

        &.is-search ::v-deep .notification-badge {
          top: 0.75rem;
          right: 0.75rem;
        }

        ::v-deep #notificationsMenu {
          .btn {
            border-radius: 30px;
            width: 44px;
            height: 44px;

            .acv-icon {
              color: theme('colors.black.500');
            }

            &:hover {
              background-color: rgb(0 0 0 / 10%);
            }
          }

          .dropdown-header {
            border: none;

            .col {
              line-height: 24px;

              h6 {
                font-size: 20px;
                font-weight: bold;
              }
            }
          }

          .notifications-item {
            border-bottom: none;
          }

          .dropdown-menu {
            margin-top: 5px;

            .view-all-btn {
              a {
                &.dropdown-item {
                  border: none;
                  background-color: transparent;
                }
              }

              &:hover {
                text-decoration: underline;
              }
            }
          }
        }
      }
    }
  }
}
</style>
